import { normalizeProductPricesForDisplay } from './productPricing';
import {
  buildViewsMap,
  getProductId,
  getViewCountFromRow,
  mergeProductViews,
} from './pdpViews';

/** Stable id for deduping catalog rows across homepage sections. */
export function getProductCatalogKey(product) {
  if (!product) return '';
  return String(product._id ?? product.id ?? product.productId ?? '').trim();
}

function productTimestamp(product) {
  const raw = product?.datePosted ?? product?.createdAt ?? product?.updatedAt ?? 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function trendingScore(product) {
  const views = Number(
    product?.viewCount ?? product?.views ?? product?.stats?.viewCount ?? 0
  );
  const likes = Number(product?.wishlistCount ?? product?.likeCount ?? 0);
  return views + likes * 2;
}

export function sortProductsByNewest(products) {
  return [...products].sort((a, b) => productTimestamp(b) - productTimestamp(a));
}

export function sortProductsByTrendingScore(products) {
  return [...products].sort((a, b) => {
    const diff = trendingScore(b) - trendingScore(a);
    if (diff !== 0) return diff;
    return productTimestamp(b) - productTimestamp(a);
  });
}

/** Same counter as PDP badge — POST/GET `/products/:id/views` (not wishlist-weighted). */
export function sortProductsByPdpViews(products) {
  return [...products].sort((a, b) => {
    const diff = getViewCountFromRow(b) - getViewCountFromRow(a);
    if (diff !== 0) return diff;
    return productTimestamp(b) - productTimestamp(a);
  });
}

function filterValidCatalogProducts(allProducts) {
  return (Array.isArray(allProducts) ? allProducts : []).filter(
    (p) => p && getProductCatalogKey(p) && p.name
  );
}

/** Hot New = newest catalog rows (independent of trending). */
export function pickHotNewProducts(allProducts, sectionSize = 8) {
  return sortProductsByNewest(filterValidCatalogProducts(allProducts)).slice(0, sectionSize);
}

/**
 * Homepage Trending: top N by PDP view count.
 * @param {object[]} catalog — full catalog for merging leaderboard rows
 * @param {object} apiResult — `{ products?, topProducts? }` from getTrendingByPdpViews
 */
export function resolveHomeTrendingByPdpViews(catalog, apiResult, sectionSize = 8) {
  const validCatalog = filterValidCatalogProducts(catalog);
  const catalogById = new Map(validCatalog.map((p) => [getProductId(p), p]));

  const fromApi = Array.isArray(apiResult?.products) ? apiResult.products : [];
  const withNames = fromApi.filter((p) => p && getProductCatalogKey(p) && p.name);
  if (withNames.length) {
    // Backend already returns viewCount desc (+ createdAt tie-break); keep order.
    return withNames.slice(0, sectionSize);
  }

  const leaderboard = Array.isArray(apiResult?.topProducts) ? apiResult.topProducts : [];
  if (leaderboard.length) {
    const viewsMap = buildViewsMap(leaderboard);
    const ordered = [];
    const seen = new Set();
    for (const row of leaderboard) {
      const pid = String(row?.productId ?? row?._id ?? row?.id ?? '').trim();
      if (!pid || seen.has(pid)) continue;
      const base = catalogById.get(pid);
      if (!base) continue;
      seen.add(pid);
      ordered.push({
        ...base,
        pdpViewCount: viewsMap.get(pid) ?? getViewCountFromRow(row),
        views: viewsMap.get(pid) ?? getViewCountFromRow(row),
      });
      if (ordered.length >= sectionSize) break;
    }
    if (ordered.length) return ordered;
  }

  return sortProductsByPdpViews(mergeProductViews(validCatalog, new Map())).slice(
    0,
    sectionSize
  );
}

/**
 * Split one catalog fetch into non-overlapping Trending + Hot New lists.
 * Hot New = newest items; Trending = popular picks from the rest (views/likes), then next-newest fill.
 */
export function splitTrendingAndHotNew(allProducts, sectionSize = 8) {
  const valid = (Array.isArray(allProducts) ? allProducts : []).filter(
    (p) => p && getProductCatalogKey(p) && p.name
  );

  const byNew = sortProductsByNewest(valid);
  const hotNew = byNew.slice(0, sectionSize);
  const hotNewKeys = new Set(hotNew.map(getProductCatalogKey));

  const trendingFromPopular = sortProductsByTrendingScore(
    byNew.filter((p) => !hotNewKeys.has(getProductCatalogKey(p)))
  ).slice(0, sectionSize);

  if (trendingFromPopular.length >= sectionSize || byNew.length <= sectionSize) {
    return { trending: trendingFromPopular, hotNew };
  }

  // Small catalog: fill Trending with next-newest items not already in Hot New
  const trending = [...trendingFromPopular];
  const used = new Set([...hotNewKeys, ...trending.map(getProductCatalogKey)]);
  for (const p of byNew) {
    if (trending.length >= sectionSize) break;
    const key = getProductCatalogKey(p);
    if (!key || used.has(key)) continue;
    trending.push(p);
    used.add(key);
  }

  return { trending, hotNew };
}

/** True when product has an active discount (flag + lower selling price than list). */
export function isProductOnSale(product) {
  if (!product) return false;
  const prices = normalizeProductPricesForDisplay(product);
  return Boolean(prices.isOnSale && prices.originalPrice > prices.price);
}

/** Admin “Featured Product” flag on public catalog rows. */
export function isProductFeatured(product) {
  if (!product) return false;
  return Boolean(
    product.isFeatured ?? product.is_featured ?? product.featured
  );
}

/** Featured homepage rows — only `isFeatured` products, newest first. */
export function pickFeaturedProducts(catalog, sectionSize = 8) {
  const valid = (Array.isArray(catalog) ? catalog : []).filter(
    (p) => p && getProductCatalogKey(p) && p.name && isProductFeatured(p)
  );
  return sortProductsByNewest(valid).slice(0, sectionSize);
}

/**
 * Homepage Featured carousel — API list first, else filter catalog.
 * @param {object} apiResult — `{ products? }` from getHomeFeaturedProducts
 */
export function resolveHomeFeaturedProducts(catalog, apiResult, sectionSize = 8) {
  const fromApi = Array.isArray(apiResult?.products) ? apiResult.products : [];
  const withNames = fromApi.filter((p) => p && getProductCatalogKey(p) && p.name);
  if (withNames.length) {
    const flagged = withNames.filter(isProductFeatured);
    return (flagged.length ? flagged : withNames).slice(0, sectionSize);
  }
  return pickFeaturedProducts(catalog, sectionSize);
}

/** Sale rows from catalog — only genuine on-sale products; no filler when none exist. */
export function pickSaleProducts(catalog, excludeKeys, sectionSize = 8) {
  const excluded = excludeKeys instanceof Set ? excludeKeys : new Set(excludeKeys || []);
  const onSale = (Array.isArray(catalog) ? catalog : []).filter((p) => {
    const key = getProductCatalogKey(p);
    return key && p?.name && isProductOnSale(p) && !excluded.has(key);
  });
  return sortProductsByNewest(onSale).slice(0, sectionSize);
}
